// NPM Imports
import React, { useContext } from 'react'
// Quote Context
import { useQuoteContext } from '../../../hooks/useQuoteContext'
// JS Imports
import {
   formatFloatAsCurrencySymbol,
   findObjectByKeyValue,
} from '../../../common/helpers.mjs'
import { calculateActivationItemTotals } from '../../../common/calculateActivationItemTotals.mjs'
// Asset Imports
import { Description } from '../../ui-helper-elements'

/**
 * Checks if the Opportunity is prorated and if there is a difference quote, if so, it will display the total proration cost
 * while allowing the agent to hover over the total to see the breakdown of the proration cost.
 *
 * @returns Component to display based on prorated and difference quote
 */
function HoverTotalProratedCost() {
   const state = useQuoteContext()
   const { getString, getFormattedString } = state

   const isDiff = state.differenceQuote ? state.differenceQuote : false
   const isPrepayment = state.discountPrepaymentYears !== '0' || false
   const proratedProductDetails = calculateActivationItemTotals(
      state.product_list,
      state.discount_list,
      state.pricebookId,
      state.quoteType,
      state.coreProductSelected,
      state.productCoreEECount,
      state.productPerformanceEECount,
      state.productTimeTrackingEECount,
      state.productHasTimeClocks,
      state.productTimeClockDetails,
      state.productBenefitsAdministrationEECount,
      state.productPayrollEINCount,
      state.productPayrollEECount,
      state.productPayrollEECounts,
      state.productPayrollTotalStateCount,
      state.productPayrollStateCounts,
      state.professionalServiceDetails,
      state.consultingServiceDetails,
      state.impType,
      state.benAdminImpType,
      state.payrollImpType,
      state.discountBundle,
      state.discountNonProfit,
      state.discountPrepaymentYears,
      state.discountMultiEntity,
      state.discountHRMonthlyPromotion,
      state.discountDealDeskMonthly,
      state.discountReseller,
      state.discountQuickstartTraining,
      state.discountImpPromotion,
      state.discountDealDeskHRImp,
      state.discountDealDeskPayrollImp,
      state.discountSecondEIN,
      state.discountGroupImp,
      state.discountDealDeskHRImpRate,
      state.discountDealDeskQuickstartImpRate,
      state.discountDealDeskPayrollImpRate,
      state.productAdditionalStorageCount,
      state.productAdditionalJobsCount,
      state.productHistoricalDataImport,
      state.productMarketplaceListing,
      state.productSponsorship,
      state.productPaidServiceAmount,
      state.productTestAccount,
      state.activationDate,
      state.differenceQuote,
      state.accountSnowflake,
      state.coreCountryCount,
      state.promotionalDiscounts,
      state.nextInvoiceDate,
      state.currencyObject,
      state.accountBalance,
      state.monthlyTax,
      state.productEINIntegrations,
      state.featurePermissions,
      state.user_role,
      state.accountStatus,
      findObjectByKeyValue(
         state.pricebooks,
         'value',
         parseInt(state.pricebookId),
      ),
      state.currencyObjectPrevious,
      state.prepaymentActivationUseDifference,
      state.dynamicProducts,
      state.timeProductSelected,
      state.isATSOnly,
   )

   if (isPrepayment) {
      return (
         <span>
            {formatFloatAsCurrencySymbol(
               state.proratedActivationDetails.totalPrice,
               state.currencyObject,
            )}
         </span>
      )
   }

   function calculateProrationTotalColumn(totalPrice, daysToPay, daysInMonth) {
      // 1. Convert totalPrice string to a number.
      const numericTotalPrice = parseFloat(totalPrice)

      // 2. Evaluate the daysPaidInMonth fraction string.
      const numerator = parseFloat(daysToPay)
      const denominator = parseFloat(daysInMonth)

      // Calculate the proration factor.
      const prorationFactor = numerator / denominator

      // 3. Perform the multiplication.
      const result = numericTotalPrice * prorationFactor

      // Return the result, perhaps rounded for currency/practical use
      return result
   }

   return (
      <Description
         className='col col-bhr-red'
         title='Prorated Total'
         content={
            <>
               <table className='table table-condensed'>
                  <thead>
                     <tr className='hide-table-border'>
                        <th className='text-nowrap'>
                           {getString(
                              'product.HoverTotalProratedCost.Header.ProductName',
                           )}
                        </th>
                        <th className='text-nowrap'>
                           {isDiff
                              ? getString(
                                   'product.HoverTotalProratedCost.Header.TotalMonthlyDiff',
                                )
                              : getString(
                                   'product.HoverTotalProratedCost.Header.TotalMonthlyCost',
                                )}
                        </th>
                        <th className='text-nowrap'>
                           {getString(
                              'product.HoverTotalProratedCost.Header.Formula',
                           )}
                        </th>
                        <th className='text-nowrap'>
                           {getString(
                              'product.HoverTotalProratedCost.Header.ProratedTotal',
                           )}
                        </th>
                     </tr>
                  </thead>
                  <tbody>
                     {/* Core Item */}
                     {proratedProductDetails.proratedCoreTotal > 0 && (
                        <tr className='hide-table-border'>
                           <td>
                              {state.product_list[state.coreProductSelected]
                                 .ProductName || 'Main Product'}
                           </td>
                           <td>
                              {formatFloatAsCurrencySymbol(
                                 isDiff
                                    ? state.productCoreMonthlyCosts
                                         .totalPriceDifference
                                    : state.productCoreMonthlyCosts.totalPrice,
                                 state.currencyObject,
                              )}
                           </td>
                           <td>
                              {isDiff
                                 ? state.productCoreMonthlyCosts
                                      .totalPriceDifference
                                 : state.productCoreMonthlyCosts
                                      .totalPrice}{' '}
                              *{' '}
                              {state.proratedActivationDetails.daysPaidInMonth}
                           </td>
                           <td>
                              {formatFloatAsCurrencySymbol(
                                 calculateProrationTotalColumn(
                                    (isDiff
                                       ? state.productCoreMonthlyCosts
                                            .totalPriceDifference
                                       : state.productCoreMonthlyCosts
                                            .totalPrice
                                    ).toFixed(2),
                                    state.proratedActivationDetails
                                       .daysToPayAfterActivation,
                                    state.proratedActivationDetails
                                       .totalDaysInMonth,
                                 ),
                                 state.currencyObject,
                              )}
                           </td>
                        </tr>
                     )}
                     {/* Time Tracking */}
                     {proratedProductDetails.proratedTimeTrackingTotal > 0 && (
                        <tr className='hide-table-border'>
                           <td>
                              {state.product_list['timeTracking'].ProductName}
                           </td>
                           <td>
                              {formatFloatAsCurrencySymbol(
                                 isDiff
                                    ? state.productTimeTrackingMonthlyCosts
                                         .totalPriceDifference
                                    : state.productTimeTrackingMonthlyCosts
                                         .totalPrice,
                                 state.currencyObject,
                              )}
                           </td>
                           <td>
                              {isDiff
                                 ? state.productTimeTrackingMonthlyCosts
                                      .totalPriceDifference
                                 : state.productTimeTrackingMonthlyCosts
                                      .totalPrice}{' '}
                              *{' '}
                              {state.proratedActivationDetails.daysPaidInMonth}
                           </td>
                           <td>
                              {formatFloatAsCurrencySymbol(
                                 calculateProrationTotalColumn(
                                    (isDiff
                                       ? state.productTimeTrackingMonthlyCosts
                                            .totalPriceDifference
                                       : state.productTimeTrackingMonthlyCosts
                                            .totalPrice
                                    ).toFixed(2),
                                    state.proratedActivationDetails
                                       .daysToPayAfterActivation,
                                    state.proratedActivationDetails
                                       .totalDaysInMonth,
                                 ),
                                 state.currencyObject,
                              )}
                           </td>
                        </tr>
                     )}
                     {/* Additional Jobs */}
                     {proratedProductDetails.proratedAdditionalJobsTotal >
                        0 && (
                        <tr className='hide-table-border'>
                           <td>
                              {state.product_list['addJobPosting'].ProductName}
                           </td>
                           <td>
                              {formatFloatAsCurrencySymbol(
                                 isDiff
                                    ? state.AdditionalJobsCosts
                                         .totalPriceDifference
                                    : state.AdditionalJobsCosts.totalPrice,
                                 state.currencyObject,
                              )}
                           </td>
                           <td>
                              {isDiff
                                 ? state.AdditionalJobsCosts
                                      .totalPriceDifference
                                 : state.AdditionalJobsCosts.totalPrice}{' '}
                              *{' '}
                              {state.proratedActivationDetails.daysPaidInMonth}
                           </td>
                           <td>
                              {formatFloatAsCurrencySymbol(
                                 calculateProrationTotalColumn(
                                    (isDiff
                                       ? state.AdditionalJobsCosts
                                            .totalPriceDifference
                                       : state.AdditionalJobsCosts.totalPrice
                                    ).toFixed(2),
                                    state.proratedActivationDetails
                                       .daysToPayAfterActivation,
                                    state.proratedActivationDetails
                                       .totalDaysInMonth,
                                 ),
                                 state.currencyObject,
                              )}
                           </td>
                        </tr>
                     )}
                     {/* Ben Admin */}
                     {proratedProductDetails.proratedBenefitsTotal > 0 && (
                        <tr className='hide-table-border'>
                           <td>{state.product_list['benAdmin'].ProductName}</td>
                           <td>
                              {formatFloatAsCurrencySymbol(
                                 isDiff
                                    ? state
                                         .productBenefitsAdministrationMonthlyCosts
                                         .totalPriceDifference
                                    : state
                                         .productBenefitsAdministrationMonthlyCosts
                                         .totalPrice,
                                 state.currencyObject,
                              )}
                           </td>
                           <td>
                              {isDiff
                                 ? state
                                      .productBenefitsAdministrationMonthlyCosts
                                      .totalPriceDifference
                                 : state
                                      .productBenefitsAdministrationMonthlyCosts
                                      .totalPrice}{' '}
                              *{' '}
                              {state.proratedActivationDetails.daysPaidInMonth}
                           </td>
                           <td>
                              {formatFloatAsCurrencySymbol(
                                 calculateProrationTotalColumn(
                                    (isDiff
                                       ? state
                                            .productBenefitsAdministrationMonthlyCosts
                                            .totalPriceDifference
                                       : state
                                            .productBenefitsAdministrationMonthlyCosts
                                            .totalPrice
                                    ).toFixed(2),
                                    state.proratedActivationDetails
                                       .daysToPayAfterActivation,
                                    state.proratedActivationDetails
                                       .totalDaysInMonth,
                                 ),
                                 state.currencyObject,
                              )}
                           </td>
                        </tr>
                     )}
                     {/* Monthly Tax */}
                     {proratedProductDetails.proratedMonthlyTaxTotal > 0 && (
                        <tr className='hide-table-border'>
                           <td>
                              {state.product_list['monthlyTax'].ProductName}
                           </td>
                           <td>
                              {formatFloatAsCurrencySymbol(
                                 isDiff
                                    ? state.MonthlyTaxCosts.totalPriceDifference
                                    : state.MonthlyTaxCosts.totalPrice,
                                 state.currencyObject,
                              )}
                           </td>
                           <td>
                              {isDiff
                                 ? state.MonthlyTaxCosts.totalPriceDifference
                                 : state.MonthlyTaxCosts.totalPrice}{' '}
                              *{' '}
                              {state.proratedActivationDetails.daysPaidInMonth}
                           </td>
                           <td>
                              {formatFloatAsCurrencySymbol(
                                 calculateProrationTotalColumn(
                                    (isDiff
                                       ? state.MonthlyTaxCosts
                                            .totalPriceDifference
                                       : state.MonthlyTaxCosts.totalPrice
                                    ).toFixed(2),
                                    state.proratedActivationDetails
                                       .daysToPayAfterActivation,
                                    state.proratedActivationDetails
                                       .totalDaysInMonth,
                                 ),
                                 state.currencyObject,
                              )}
                           </td>
                        </tr>
                     )}
                     {/* Additional Storage */}
                     {proratedProductDetails.proratedAdditionalStorageTotal >
                        0 && (
                        <tr className='hide-table-border'>
                           <td>
                              {state.product_list['addStorage'].ProductName}
                           </td>
                           <td>
                              {formatFloatAsCurrencySymbol(
                                 isDiff
                                    ? state.AdditionalStorageCosts
                                         .totalPriceDifference
                                    : state.AdditionalStorageCosts.totalPrice,
                                 state.currencyObject,
                              )}
                           </td>
                           <td>
                              {isDiff
                                 ? state.AdditionalStorageCosts
                                      .totalPriceDifference
                                 : state.AdditionalStorageCosts.totalPrice}{' '}
                              *{' '}
                              {state.proratedActivationDetails.daysPaidInMonth}
                           </td>
                           <td>
                              {formatFloatAsCurrencySymbol(
                                 calculateProrationTotalColumn(
                                    (isDiff
                                       ? state.AdditionalStorageCosts
                                            .totalPriceDifference
                                       : state.AdditionalStorageCosts.totalPrice
                                    ).toFixed(2),
                                    state.proratedActivationDetails
                                       .daysToPayAfterActivation,
                                    state.proratedActivationDetails
                                       .totalDaysInMonth,
                                 ),
                                 state.currencyObject,
                              )}
                           </td>
                        </tr>
                     )}
                     {/* Test Account */}
                     {proratedProductDetails.testAccountTotal > 0 && (
                        <tr className='hide-table-border'>
                           <td>
                              {state.product_list['testAccount'].ProductName}
                           </td>
                           <td>
                              {formatFloatAsCurrencySymbol(
                                 isDiff
                                    ? state.TestAccountCosts
                                         .totalPriceDifference
                                    : state.TestAccountCosts.totalPrice,
                                 state.currencyObject,
                              )}
                           </td>
                           <td>
                              {isDiff
                                 ? state.TestAccountCosts.totalPriceDifference
                                 : state.TestAccountCosts.totalPrice}{' '}
                              *{' '}
                              {state.proratedActivationDetails.daysPaidInMonth}
                           </td>
                           <td>
                              {formatFloatAsCurrencySymbol(
                                 calculateProrationTotalColumn(
                                    (isDiff
                                       ? state.TestAccountCosts
                                            .totalPriceDifference
                                       : state.TestAccountCosts.totalPrice
                                    ).toFixed(2),
                                    state.proratedActivationDetails
                                       .daysToPayAfterActivation,
                                    state.proratedActivationDetails
                                       .totalDaysInMonth,
                                 ),
                                 state.currencyObject,
                              )}
                           </td>
                        </tr>
                     )}
                     {/* Performance Manager */}
                     {proratedProductDetails.proratedPerformanceTotal > 0 && (
                        <tr className='hide-table-border'>
                           <td>
                              {state.product_list['performanceMan'].ProductName}
                           </td>
                           <td>
                              {formatFloatAsCurrencySymbol(
                                 isDiff
                                    ? state.productPerformanceMonthlyCosts
                                         .totalPriceDifference
                                    : state.productPerformanceMonthlyCosts
                                         .totalPrice,
                                 state.currencyObject,
                              )}
                           </td>
                           <td>
                              {isDiff
                                 ? state.productPerformanceMonthlyCosts
                                      .totalPriceDifference
                                 : state.productPerformanceMonthlyCosts
                                      .totalPrice}{' '}
                              *{' '}
                              {state.proratedActivationDetails.daysPaidInMonth}
                           </td>
                           <td>
                              {formatFloatAsCurrencySymbol(
                                 calculateProrationTotalColumn(
                                    (isDiff
                                       ? state.productPerformanceMonthlyCosts
                                            .totalPriceDifference
                                       : state.productPerformanceMonthlyCosts
                                            .totalPrice
                                    ).toFixed(2),
                                    state.proratedActivationDetails
                                       .daysToPayAfterActivation,
                                    state.proratedActivationDetails
                                       .totalDaysInMonth,
                                 ),
                                 state.currencyObject,
                              )}
                           </td>
                        </tr>
                     )}
                     {/* Total */}
                     <tr className='hide-table-border'>
                        <td>
                           <b>
                              {getString(
                                 'product.HoverTotalProratedCost.Header.TotalProratedCost',
                              )}
                           </b>
                        </td>
                        <td></td>
                        <td></td>
                        <td>
                           <b>
                              {formatFloatAsCurrencySymbol(
                                 state.proratedActivationDetails.totalPrice,
                                 state.currencyObject,
                              )}
                           </b>
                        </td>
                     </tr>
                  </tbody>
               </table>
               <p>
                  {getFormattedString(
                     'product.HoverTotalProratedCost.Footer.PrepaymentTerms',
                     { additionalText: isDiff ? ' difference' : '' },
                  )}
               </p>
            </>
         }
      >
         <span className='hoverable'>
            {formatFloatAsCurrencySymbol(
               state.proratedActivationDetails.totalPrice,
               state.currencyObject,
            )}
         </span>
      </Description>
   )
}

export default HoverTotalProratedCost
